import { useMemo, useState } from "react";
import { useMySoorAlerts } from "../../../../hooks/useMySoorAlerts";
import { useResolveWorklistAlert } from "../../../../hooks/useResolveWorklistAlert";
import UiModalAlert from "../../../../components/modal-alert/ModalAlert.component";
import { Task } from "../../../../models/entities/task.model";
import { getSessionStorageUser } from "../../../../services/session/auth.service";
import WorklistSection from "../WorklistSection";
import { useWorklist } from "../WorklistContext";
import { groupSoorAlerts } from "../worklist.service";
import CriticalAlertRow from "../critical-alerts/CriticalAlertRow";

export const SOOR_HIGH_SECTION_ID = "soor-alerts-high";
export const SOOR_LOW_SECTION_ID = "soor-alerts-low";

/**
 * "Sustained out-of-range alerts" — split into two separate sections (KAN-2641).
 *
 * High priority (severity-escalated) and Low priority, each as its own
 * WorklistSection with its own collapsible header and count badge. High starts
 * expanded, Low starts collapsed (Maria 2026-10-16). The row numbering continues
 * across sections via WorklistContext (no manual offset calculation needed).
 * Oldest-first ordering and amber left edge.
 *
 * The alert metadata carries the out-of-range measurements; every row includes
 * the average label (e.g. "Systolic — Avg (152)") if metadata is present.
 * Legacy rows without metadata omit the average.
 *
 * See karchitecture/planning/kan-2641-soor-homepage-section.md for the design.
 */
const SoorAlertsSection = () => {
	const sessionUser = getSessionStorageUser();
	const { alerts, isLoading, isError, invalidate, hideAlert } = useMySoorAlerts(sessionUser?.id);
	const { resolve, isPending } = useResolveWorklistAlert(hideAlert, sessionUser?.id);
	const { getRowOffset } = useWorklist();

	const [alertModal, setAlertModal] = useState<{ entity: Task | null; isVisible: boolean }>({
		entity: null,
		isVisible: false,
	});

	const edit = (alert: Task) => setAlertModal({ entity: alert, isVisible: true });
	const cancelAlertModal = () => setAlertModal((prev) => ({ ...prev, isVisible: false }));
	const completeAlertModal = () => {
		setAlertModal((prev) => ({ ...prev, isVisible: false }));
		invalidate();
	};

	const grouped = useMemo(() => groupSoorAlerts(alerts), [alerts]);

	/**
	 * Render a list of rows for one priority group.
	 * Row numbers continue from the offset published by the section to the context.
	 */
	const renderRows = (rows: Task[], sectionId: string) => {
		const sectionOffset = getRowOffset(sectionId);
		return (
			<ul className="worklist-rows">
				{rows.map((alert, position) => (
					<CriticalAlertRow
						key={alert.id}
						variant="soor"
						alert={alert}
						index={sectionOffset + position + 1}
						onResolve={resolve}
						onEdit={edit}
						isResolving={isPending}
					/>
				))}
			</ul>
		);
	};

	return (
		<>
			<WorklistSection
				sectionId={SOOR_HIGH_SECTION_ID}
				titleKey="HOME.WORKLIST.SOOR.TITLE_HIGH"
				count={grouped.high.length}
				isLoading={isLoading}
				isError={isError}
				isEmpty={grouped.high.length === 0}
				loadingMessageKey="HOME.WORKLIST.SOOR.LOADING"
				errorMessageKey="HOME.WORKLIST.SOOR.ERROR"
				defaultExpanded={true}
			>
				{renderRows(grouped.high, SOOR_HIGH_SECTION_ID)}
			</WorklistSection>

			<WorklistSection
				sectionId={SOOR_LOW_SECTION_ID}
				titleKey="HOME.WORKLIST.SOOR.TITLE_LOW"
				count={grouped.low.length}
				isLoading={isLoading}
				isError={isError}
				isEmpty={grouped.low.length === 0}
				loadingMessageKey="HOME.WORKLIST.SOOR.LOADING"
				errorMessageKey="HOME.WORKLIST.SOOR.ERROR"
				defaultExpanded={false}
			>
				{renderRows(grouped.low, SOOR_LOW_SECTION_ID)}
			</WorklistSection>

			{alertModal.isVisible && alertModal.entity && (
				<UiModalAlert
					entity={alertModal.entity}
					isVisible={alertModal.isVisible}
					onCancel={cancelAlertModal}
					onComplete={completeAlertModal}
				/>
			)}
		</>
	);
};

export default SoorAlertsSection;
